import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Calendar, UtensilsCrossed, CheckCircle, Clock, XCircle, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ParentMealReservation({ user }) {
  const [loading, setLoading] = useState(false);
  const [menus, setMenus] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [children, setChildren] = useState([]);
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [selectedChild, setSelectedChild] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [menusRes, reservationsRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/menus`),
        axios.get(`${API}/parent/meal-reservations`),
        axios.get(`${API}/parent/dashboard`)
      ]);

      // Filtrer les menus futurs uniquement
      const futureMenus = menusRes.data.filter(menu => {
        const menuDate = new Date(menu.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return menuDate >= today;
      });

      setMenus(futureMenus);
      setReservations(reservationsRes.data);
      setChildren(dashboardRes.data.children || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleReserveMeal = async () => {
    if (!selectedChild || !selectedMenu) {
      toast.error('Veuillez sélectionner un enfant et un menu');
      return;
    }

    try {
      await axios.post(`${API}/parent/meal-reservations`, {
        student_id: selectedChild,
        menu_day_id: selectedMenu.id,
        quantity: 1
      });

      toast.success('Demande de réservation envoyée avec succès !');
      setReservationDialogOpen(false);
      setSelectedChild('');
      setSelectedMenu(null);
      fetchData();
    } catch (error) {
      console.error('Error creating reservation:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
      case 'ACTIVE':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Validé</Badge>;
      case 'REJECTED':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Refusé</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Menus disponibles */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <UtensilsCrossed className="w-5 h-5 mr-2" />
            Menus disponibles
          </CardTitle>
          <CardDescription>
            Réservez des repas pour vos enfants
          </CardDescription>
        </CardHeader>
        <CardContent>
          {menus.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucun menu disponible pour le moment</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {menus.map((menu) => (
                <Card key={menu.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      <span className="text-sm text-gray-600">
                        {formatDate(menu.date)}
                      </span>
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{menu.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">{menu.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-green-600">
                        {menu.price?.toLocaleString() || '5,000'} GNF
                      </span>
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedMenu(menu);
                          setReservationDialogOpen(true);
                        }}
                      >
                        Réserver
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mes réservations */}
      <Card>
        <CardHeader>
          <CardTitle>Mes réservations</CardTitle>
          <CardDescription>
            Historique de vos demandes de réservation
          </CardDescription>
        </CardHeader>
        <CardContent>
          {reservations.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucune réservation pour le moment</p>
          ) : (
            <div className="space-y-3">
              {reservations.map((reservation) => (
                <Card key={reservation.id} className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-semibold">{reservation.menu_title}</h4>
                          {getStatusBadge(reservation.status)}
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Élève :</strong> {reservation.student_name}
                        </p>
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Date du repas :</strong> {formatDate(reservation.menu_date)}
                        </p>
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Quantité :</strong> {reservation.quantity}
                        </p>
                        <p className="text-sm font-semibold text-green-600">
                          Montant : {reservation.total_amount?.toLocaleString()} GNF
                        </p>
                      </div>
                      {reservation.status === 'ACTIVE' && reservation.invoice_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.location.href = '/dashboard/billing'}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Voir facture
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Demandé le {formatDate(reservation.requested_at)}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de réservation */}
      <Dialog open={reservationDialogOpen} onOpenChange={setReservationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Réserver un repas</DialogTitle>
            <DialogDescription>
              {selectedMenu && (
                <>
                  {selectedMenu.name} - {formatDate(selectedMenu.date)}
                  <br />
                  Prix : {selectedMenu.price?.toLocaleString() || '5,000'} GNF
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="child">Sélectionner l'enfant</Label>
              <Select value={selectedChild} onValueChange={setSelectedChild}>
                <SelectTrigger id="child">
                  <SelectValue placeholder="Choisir un enfant" />
                </SelectTrigger>
                <SelectContent>
                  {children.map((child) => (
                    <SelectItem key={child.id} value={child.id}>
                      {child.user_info?.first_name} {child.user_info?.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Note :</strong> Votre demande sera vérifiée par le trésorier ou le secrétaire. 
                Une facture vous sera envoyée une fois la réservation validée.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setReservationDialogOpen(false);
                  setSelectedChild('');
                  setSelectedMenu(null);
                }}
              >
                Annuler
              </Button>
              <Button onClick={handleReserveMeal}>
                Confirmer la réservation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ParentMealReservation;
